import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowRight, Eye, EyeOff, Film, Loader2, Lock, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { API_HOST } from '../utils/constants';
import { getLoginLandingPath } from '../utils/userRoles';

const fallbackShowcase = [
  { type: '作品', imageUrl: '', videoUrl: '', accent: '#d16b28' },
  { type: '视频', imageUrl: '', videoUrl: '', accent: '#9b5cff' },
  { type: '角色', imageUrl: '', videoUrl: '', accent: '#e05f91' },
  { type: '场景', imageUrl: '', videoUrl: '', accent: '#46b99a' },
  { type: '故事板', imageUrl: '', videoUrl: '', accent: '#5aa8ff' },
  { type: '资产', imageUrl: '', videoUrl: '', accent: '#d0b14f' },
];

const fallbackGradients = [
  'linear-gradient(145deg, var(--color-bg-secondary), var(--color-bg-overlay))',
  'linear-gradient(160deg, var(--color-bg-tertiary), var(--color-bg-primary))',
  'linear-gradient(135deg, var(--color-bg-primary), var(--color-bg-overlay))',
];

const decodeStoredUsername = () => {
  const raw = localStorage.getItem('app_user_id') || '';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const normalizeMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith('data:')) return trimmed;
  if (trimmed.startsWith('/')) return `${API_HOST}${trimmed}`;
  return `${API_HOST}/${trimmed}`;
};

const isVideoUrl = (url) => /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url || '');

const getMediaUrl = (item) => normalizeMediaUrl(item?.videoUrl || item?.mediaUrl || '');

const getPosterUrl = (item) => normalizeMediaUrl(item?.imageUrl || item?.thumbnailUrl || item?.posterUrl || '');

const buildMediaPool = (items) => {
  const withMedia = items
    .map((item) => ({
      ...item,
      videoUrl: getMediaUrl(item),
      imageUrl: getPosterUrl(item),
    }))
    .filter((item) => item.videoUrl || item.imageUrl);
  const videos = withMedia.filter((item) => item.videoUrl || isVideoUrl(item.imageUrl));
  return (videos.length >= 3 ? videos : withMedia).concat(fallbackShowcase);
};

const mediaAt = (items, index) => items[((index % items.length) + items.length) % items.length] || fallbackShowcase[0];

const ShowcaseMedia = ({ item, index }) => {
  const videoUrl = getMediaUrl(item) || (isVideoUrl(item?.imageUrl) ? getPosterUrl(item) : '');
  const imageUrl = getPosterUrl(item);
  const accent = item?.accent || '#d16b28';
  const fallback = fallbackGradients[index % fallbackGradients.length];

  return (
    <div className="absolute inset-0" style={{ background: fallback }}>
      {videoUrl ? (
        <video
          key={videoUrl}
          className="h-full w-full object-cover"
          src={videoUrl}
          poster={imageUrl || undefined}
          muted
          autoPlay
          loop
          playsInline
          preload="metadata"
        />
      ) : imageUrl ? (
        <img
          key={imageUrl}
          src={imageUrl}
          alt=""
          className="h-full w-full object-cover"
          loading="eager"
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      ) : null}
      <div
        className="absolute inset-0"
        style={{
          // 功能色：媒体展示图上的白色高光 + 黑色压暗遮罩（暗色舞台固定视觉），不接入主题变量
          background:
            'radial-gradient(circle at 50% 42%, rgba(255,255,255,0.18), transparent 28%), linear-gradient(180deg, rgba(255,255,255,0.08), transparent 26%), linear-gradient(0deg, rgba(0,0,0,0.42), rgba(0,0,0,0.06) 48%, rgba(0,0,0,0.38))',
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-1/3"
        style={{ background: `linear-gradient(0deg, ${accent}44, transparent)` }}
      />
    </div>
  );
};

const CinemaPanel = ({ item, index, className, style }) => (
  <div
    className={`absolute overflow-hidden border border-white/10 bg-white/[0.04] shadow-[0_40px_140px_rgba(0,0,0,0.65)] ${className}`}
    style={style}
    aria-hidden="true"
  >
    <ShowcaseMedia item={item} index={index} />
  </div>
);

const ShowcaseStage = ({ items, frame }) => {
  const pool = useMemo(() => buildMediaPool(items), [items]);
  const left = mediaAt(pool, frame);
  const middle = mediaAt(pool, frame + 1);
  const right = mediaAt(pool, frame + 2);

  return (
    <div className="absolute inset-0 overflow-hidden bg-[#050505]" aria-hidden="true">
      <div
        className="absolute inset-0 opacity-[0.16]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '34px 34px',
        }}
      />
      <CinemaPanel
        item={right}
        index={frame + 2}
        className="inset-0 h-full w-full scale-105 !rounded-none !border-0 !bg-transparent opacity-30 !shadow-none"
        style={{ filter: 'blur(24px)' }}
      />
      <CinemaPanel
        item={left}
        index={frame}
        className="left-[-5vw] top-1/2 h-[80vh] w-[86vw] rounded-2xl opacity-70 sm:w-[80vw] lg:h-[82vh] lg:w-[57vw] lg:rounded-3xl"
        style={{ transform: 'translateY(-50%)' }}
      />
      <CinemaPanel
        item={right}
        index={frame + 1}
        className="right-[-5vw] top-1/2 hidden h-[68vh] w-[34vw] rounded-3xl opacity-35 lg:block"
        style={{ transform: 'translate(-20%, -50%) scale(0.98)' }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(90deg, rgba(0,0,0,0.52), rgba(0,0,0,0.14) 28%, rgba(0,0,0,0.16) 68%, rgba(0,0,0,0.58)), linear-gradient(180deg, rgba(8,8,10,0.66), rgba(0,0,0,0.18) 44%, rgba(0,0,0,0.78))',
        }}
      />
    </div>
  );
};

const LoginPage = () => {
  const { loginWithPassword } = useUser();
  const [username, setUsername] = useState(decodeStoredUsername);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [setupMode, setSetupMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showcaseItems, setShowcaseItems] = useState([]);
  const [showcaseFrame, setShowcaseFrame] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_HOST}/api/public/login-showcase`, {
      headers: { Accept: 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((json) => {
        if (cancelled) return;
        const items = Array.isArray(json?.data?.items) ? json.data.items : [];
        setShowcaseItems(items.filter((item) => item?.videoUrl || item?.imageUrl));
      })
      .catch(() => {
        if (!cancelled) setShowcaseItems([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setShowcaseFrame((value) => value + 1);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const name = username.trim();
    if (!name) {
      setError('请输入用户名');
      return;
    }
    if (!password) {
      setError(setupMode ? '请设置密码' : '请输入密码');
      return;
    }
    if (setupMode && password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }
    setLoading(true);
    try {
      const data = await loginWithPassword({ username: name, password, firstSetup: setupMode });
      window.location.href = getLoginLandingPath(data.user);
    } catch (err) {
      if (err.code === 409 || err.data?.reason === 'PASSWORD_NOT_SET') {
        setSetupMode(true);
        setError('该账号还未设置密码，请设置后进入系统');
      } else {
        setError(err.message || '登录失败');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-black text-white"
      style={{ color: 'var(--color-text-primary)' }}
    >
      {/* 功能色：登录页影院舞台为固定暗色视觉（bg-black + #050505 展台），
          舞台上的白色文字/白色透明度高光与黑色遮罩在任何主题下保持不变，不接入主题变量；
          仅表单面板及输入控件接入主题变量。 */}
      <ShowcaseStage items={showcaseItems} frame={showcaseFrame} />

      <div className="pointer-events-none absolute left-1/2 top-8 z-10 flex -translate-x-1/2 items-center gap-3 text-white">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-lg"
          style={{
            backgroundColor: 'var(--color-bg-tertiary)',
            border: '1px solid var(--color-border-primary)',
            color: 'var(--color-text-primary)',
          }}
        >
          <Film size={18} />
        </span>
        <span className="text-xl font-black tracking-normal sm:text-2xl">短剧创作者中心</span>
      </div>

      <section className="relative z-10 flex min-h-screen items-center justify-center px-4 pt-20 pb-10">
        <div className="grid w-full max-w-5xl items-center gap-8 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 xl:gap-10">
          <div className="hidden lg:block">
            <p className="mb-5 text-sm font-bold" style={{ color: 'var(--color-accent-primary)' }}>
              加入我们
            </p>
            <h1 className="max-w-lg text-5xl font-black leading-tight tracking-normal text-white">
              让短剧创作进入可控生产线
            </h1>
            <p className="mt-6 max-w-md text-sm leading-7 text-white/60">
              剧本、角色、场景、道具、故事板和视频粗剪集中管理，创作资产沉淀为可复用的项目能力。
            </p>
          </div>

        <form
          onSubmit={submit}
          className="relative z-10 ml-auto w-full max-w-[390px] rounded-2xl p-7 shadow-[0_28px_90px_rgba(0,0,0,0.5)]"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)',
          }}
        >
          <div className="mb-7">
            <div
              className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                border: '1px solid var(--color-border-primary)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <Lock size={20} />
            </div>
            <h1 className="text-2xl font-bold tracking-normal">
              {setupMode ? '设置密码' : '登录'}
            </h1>
            <p className="mt-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {setupMode ? '首次进入前需要设置登录密码。' : '输入用户名和密码进入工作台。'}
            </p>
          </div>

          <label htmlFor="login-username" className="mb-2 block text-xs font-bold" style={{ color: 'var(--color-text-muted)' }}>
            用户名
          </label>
          <div className="relative mb-4">
            <User
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--color-text-muted)' }}
            />
            <input
              id="login-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="h-11 w-full rounded-lg pl-10 pr-3 text-base outline-none"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                border: '1px solid var(--color-border-primary)',
                color: 'var(--color-text-primary)',
              }}
              placeholder="请输入用户名"
              autoFocus
            />
          </div>

          <label htmlFor="login-password" className="mb-2 block text-xs font-bold" style={{ color: 'var(--color-text-muted)' }}>
            {setupMode ? '新密码' : '密码'}
          </label>
          <div className="relative mb-4">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 w-full rounded-lg px-3 pr-11 text-base outline-none"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                border: '1px solid var(--color-border-primary)',
                color: 'var(--color-text-primary)',
              }}
              placeholder="请输入密码"
            />
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              className="absolute right-0.5 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg"
              style={{ color: 'var(--color-text-muted)' }}
              aria-label={showPassword ? '隐藏密码' : '显示密码'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {setupMode && (
            <>
              <label htmlFor="login-confirm-password" className="mb-2 block text-xs font-bold" style={{ color: 'var(--color-text-muted)' }}>
                确认密码
              </label>
              <input
                id="login-confirm-password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mb-4 h-11 w-full rounded-lg px-3 text-base outline-none"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  border: '1px solid var(--color-border-primary)',
                  color: 'var(--color-text-primary)',
                }}
                placeholder="再次输入密码"
              />
            </>
          )}

          {error && (
            <div
              className="mb-4 flex items-start gap-2 rounded-lg px-3 py-2 text-sm animate-in fade-in slide-in-from-top-1 duration-200"
              style={{
                backgroundColor: 'var(--color-red-light)',
                color: 'var(--color-error)',
                border: '1px solid var(--color-error)',
              }}
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg font-bold transition-all hover:brightness-110 disabled:opacity-60"
            style={{ backgroundColor: 'var(--color-accent-primary)', color: 'var(--color-accent-contrast)' }}
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? '处理中...' : setupMode ? '设置并进入' : '进入工作台'}
            {!loading && <ArrowRight size={16} />}
          </button>
        </form>
        </div>
      </section>
      <footer
        className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center justify-center gap-3 whitespace-nowrap text-xs"
        style={{ color: 'rgba(255,255,255,0.42)' }}
      >
        <Link to="/ai-ownership-agreement" className="transition-colors hover:text-white">
          AI 内容权属说明
        </Link>
        <span aria-hidden="true">·</span>
        <a
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noreferrer"
          className="transition-colors hover:text-white"
        >
          蜀ICP备2026031582号
        </a>
      </footer>
    </div>
  );
};

export default LoginPage;
